/**
 * src/components/spatial-bindings/SpatialObjectOrganizationPanel.tsx
 * The chain's view of one object's organization, shown in the spatial
 * object editor next to GeoLink's own organization field.
 *
 * The two fields sitting side by side is deliberate, not an oversight.
 * GeoLink's `metadata.organization_id` is a searchable pointer with no
 * relationship type, no approval step and no signature behind it; the
 * registry binding is the authoritative one. Hiding the difference would
 * be worse than showing it, because they can and do disagree — so the
 * panel names the disagreement instead of picking a winner silently.
 *
 * Actions are enabled from `can_manage_object`, read from the chain for
 * the connected wallet, not from which overlay the operator opened. The
 * backend re-checks country authority before it will build an XDR at all,
 * and the contract checks again at signing; this only decides whether a
 * button looks available.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getOrganization,
  shortOrgId,
  type Organization,
} from '../../v2/services/organizationService';
import {
  buildApproveBinding,
  buildCancelReassign,
  buildCompleteReassign,
  buildRemoveBinding,
  buildSetRelationship,
  buildSuspendBinding,
  getSpatialObject,
  RELATIONSHIP,
  RELATIONSHIP_LABEL,
  type RelationshipName,
  type SpatialObjectRecord,
  type SpatialSigner,
} from '../../v2/services/spatialRegistryService';
import {
  bindingStatusLabel,
  deriveEligibility,
  describeLimitations,
  objectStatusLabel,
  reconciliationState,
  relationshipLabel,
  RECONCILIATION_MESSAGE,
} from '../../v2/services/spatialBindingModel';
import {
  Banner, BindingStatusPill, button, CapabilityList, hint, input, mono,
  ObjectStatusPill, Pill, sectionTitle,
} from './bindingUi';
import {
  TX_PHASE_LABEL, useObjectAuthority, useSpatialRegistryConfig, useSpatialWrite,
} from './useSpatialWrite';
import { BindOrganizationDialog } from './BindOrganizationDialog';

export interface SpatialObjectOrganizationPanelProps {
  /** GeoLink UUID of the object being edited. Null for an unsaved object. */
  objectId: string | null;
  objectName?: string | null;
  objectType?: string | null;
  countryIso2?: string | null;
  parentObjectId?: string | null;
  /** What GeoLink currently records, so divergence can be named. */
  geoLinkOrganizationId?: string | null;
  signer: SpatialSigner | null;
  walletAddress: string | null;
  networkPassphrase?: string;
  /** Host's own view of whether this operator administers anything here. */
  canAdminister?: boolean;
}

export const SpatialObjectOrganizationPanel: React.FC<SpatialObjectOrganizationPanelProps> = ({
  objectId,
  objectName = null,
  objectType = null,
  countryIso2 = null,
  parentObjectId = null,
  geoLinkOrganizationId = null,
  signer,
  walletAddress,
  networkPassphrase,
  canAdminister = false,
}) => {
  const { configured } = useSpatialRegistryConfig();
  const write = useSpatialWrite(signer, networkPassphrase);

  const [record, setRecord] = useState<SpatialObjectRecord | null>(null);
  const [org, setOrg] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(false);
  const [reloads, setReloads] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [relationshipDraft, setRelationshipDraft] = useState<RelationshipName | ''>('');

  const { authority } = useObjectAuthority(objectId, walletAddress, reloads);

  const load = useCallback(async () => {
    if (!objectId || !configured) { setRecord(null); setOrg(null); return; }
    setLoading(true);
    try {
      const r = await getSpatialObject(objectId);
      setRecord(r);
      const holder = r?.binding?.organizationId ?? r?.effectiveOrganizationId ?? null;
      setOrg(holder ? await getOrganization(holder) : null);
    } finally {
      setLoading(false);
    }
  }, [objectId, configured]);

  useEffect(() => { void load(); }, [load]);

  const refresh = useCallback(async () => {
    await load();
    setReloads((n) => n + 1);
  }, [load]);

  const binding = record?.binding ?? null;
  const status = binding?.statusName ?? 'none';
  const eligibility = useMemo(() => deriveEligibility(org), [org]);
  const limitations = useMemo(() => describeLimitations(org, eligibility), [org, eligibility]);
  const state = reconciliationState(record, geoLinkOrganizationId);

  /**
   * Whether to offer writes at all.
   *
   * `canManageObject` is the chain's own answer and is preferred. It is
   * false for an object with no binding yet — nobody manages an unheld
   * object — so the host's `canAdminister` is what opens the first bind on
   * a fresh object. Both are hints; neither is enforcement.
   */
  const mayWrite = !!signer && !!walletAddress
    && (authority?.canManageObject || canAdminister);

  const run = useCallback(
    (build: () => Promise<{ xdr: string; latestLedger: number; objectId: string; objectUuid: string | null }>,
     successMessage: string,
     mirrorOrgId?: string | null) => {
      if (!objectId) return;
      void write.run({
        build,
        successMessage,
        mirror: mirrorOrgId === undefined ? null : { objectId, organizationId: mirrorOrgId },
        refresh,
      });
    },
    [objectId, write, refresh],
  );

  if (!objectId) return null;
  if (configured === null) return null;

  if (!configured) {
    return (
      <PanelShell>
        <div style={hint}>
          The spatial registry contract is not configured on this server, so on-chain
          ownership is unavailable. The GeoLink organization field above still works.
        </div>
      </PanelShell>
    );
  }

  return (
    <PanelShell>
      {loading && !record && <div style={hint}>Reading the chain…</div>}

      {RECONCILIATION_MESSAGE[state] && (
        <Banner tone={state === 'chain_only' ? 'info' : 'warn'}>
          {RECONCILIATION_MESSAGE[state]}
        </Banner>
      )}

      {/* Object state */}
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
        {record
          ? <ObjectStatusPill status={record.statusName} label={objectStatusLabel(record.statusName)} />
          : <Pill tone="neutral">Not registered on chain</Pill>}
        {binding && (
          <BindingStatusPill status={binding.statusName} label={bindingStatusLabel(binding.statusName)} />
        )}
        {binding && <Pill tone="info">{relationshipLabel(binding.relationship)}</Pill>}
        {record?.inherited && <Pill tone="neutral">Inherited from parent</Pill>}
        {record?.pendingTransfer && <Pill tone="warn">Reassignment pending</Pill>}
        {record?.canReceive && <Pill tone="good">Can receive funds</Pill>}
      </div>

      {!record && (
        <div style={{ ...hint, marginBottom: '8px' }}>
          This object exists in GeoLink only. Assigning an organization will register
          it in the spatial registry first.
        </div>
      )}

      {/* Organization */}
      {org ? (
        <div style={{
          border: '1px solid #eee', borderRadius: '4px', padding: '8px 10px', marginBottom: '8px',
        }}>
          <div style={{ fontSize: '13px', fontWeight: 600 }}>{org.name || '(unnamed)'}</div>
          <div style={{ ...hint, ...mono, marginBottom: '6px' }}>
            {shortOrgId(org.id)}… · {org.alpha2 || org.numericCode || '—'} · {org.statusName}
            {record?.inherited && record.parentUuid
              ? ` · via parent ${record.parentUuid.slice(0, 8)}…`
              : ''}
          </div>
          <CapabilityList
            canOwn={eligibility.canOwnObjects}
            canOperate={eligibility.canOperateObjects}
            canReceive={eligibility.canReceiveFunds}
            compact
          />
          {limitations.length > 0 && (
            <ul style={{ ...hint, margin: '6px 0 0 0', paddingLeft: '18px' }}>
              {limitations.map((l) => <li key={l} style={{ marginBottom: '2px' }}>{l}</li>)}
            </ul>
          )}
          {binding?.assignedBy && (
            <div style={{ ...hint, marginTop: '6px' }}>
              Approved by {binding.assignedBy.slice(0, 6)}…{binding.assignedBy.slice(-4)}
              {binding.assignedAt ? ` at ledger time ${binding.assignedAt}` : ''}
            </div>
          )}
        </div>
      ) : (
        record && <div style={{ ...hint, marginBottom: '8px' }}>No organization is bound on chain.</div>
      )}

      {record?.pendingTransfer && (
        <Banner tone="warn">
          A reassignment from {record.pendingTransfer.fromOrg?.slice(0, 8)}… to{' '}
          {record.pendingTransfer.toOrg?.slice(0, 8)}… is proposed. Completing it clears
          every delegated operator on this object.
        </Banner>
      )}

      {record?.inherited && (
        <div style={{ ...hint, marginBottom: '8px' }}>
          This organization comes from the parent object, not from a binding on this
          one. Binding an organization here overrides that inheritance for this object
          and everything below it.
        </div>
      )}

      {/* Transaction state */}
      {write.state.error && <Banner tone="error">{write.state.error}</Banner>}
      {write.state.warning && <Banner tone="warn">{write.state.warning}</Banner>}
      {write.state.notice && <Banner tone="ok">{write.state.notice}</Banner>}
      {write.busy && <Banner tone="info">{TX_PHASE_LABEL[write.state.phase]}</Banner>}

      {/* Actions */}
      {mayWrite ? (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setDialogOpen(true)}
            disabled={write.busy}
            style={button(!write.busy)}
          >
            {binding && status !== 'removed' ? 'Change organization' : 'Assign organization'}
          </button>

          {(status === 'proposed' || status === 'suspended') && walletAddress && (
            <button
              onClick={() => run(
                () => buildApproveBinding(objectId, walletAddress),
                status === 'suspended' ? 'Binding reinstated' : 'Binding approved',
                binding?.organizationId ?? null,
              )}
              disabled={write.busy}
              style={button(!write.busy)}
            >
              {status === 'suspended' ? 'Reinstate binding' : 'Approve binding'}
            </button>
          )}

          {status === 'approved' && walletAddress && (
            <button
              onClick={() => run(
                () => buildSuspendBinding(objectId, walletAddress),
                'Binding suspended',
              )}
              disabled={write.busy}
              style={button(!write.busy, 'warn')}
            >
              Suspend binding
            </button>
          )}

          {binding && status !== 'removed' && walletAddress && (
            <button
              onClick={() => run(
                () => buildRemoveBinding(objectId, walletAddress),
                'Binding removed',
                null,
              )}
              disabled={write.busy}
              style={button(!write.busy, 'danger')}
            >
              Remove binding
            </button>
          )}

          {record?.pendingTransfer && walletAddress && (
            <>
              <button
                onClick={() => run(
                  () => buildCompleteReassign(objectId, walletAddress),
                  'Reassignment completed',
                  record.pendingTransfer?.toOrg ?? null,
                )}
                disabled={write.busy}
                style={button(!write.busy, 'warn')}
              >
                Complete reassignment
              </button>
              <button
                onClick={() => run(
                  () => buildCancelReassign(objectId, walletAddress),
                  'Reassignment cancelled',
                )}
                disabled={write.busy}
                style={button(!write.busy, 'plain')}
              >
                Cancel reassignment
              </button>
            </>
          )}
        </div>
      ) : (
        <div style={hint}>
          {signer && walletAddress
            ? 'This wallet is not recorded as an administrator for this object, so '
              + 'binding actions are unavailable.'
            : 'Connect an administrator wallet to change this object\'s organization.'}
        </div>
      )}

      {/* Relationship change, only meaningful on a live binding */}
      {mayWrite && binding && status !== 'removed' && walletAddress && (
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', marginTop: '8px' }}>
          <select
            value={relationshipDraft || binding.relationshipName || 'owner'}
            onChange={(e) => setRelationshipDraft(e.target.value as RelationshipName)}
            style={{ ...input, flex: 1 }}
          >
            {(Object.keys(RELATIONSHIP) as RelationshipName[]).map((name) => (
              <option key={name} value={name}>{RELATIONSHIP_LABEL[name]}</option>
            ))}
          </select>
          <button
            onClick={() => {
              const next = relationshipDraft || binding.relationshipName;
              if (!next) return;
              run(
                () => buildSetRelationship(objectId, walletAddress, next),
                `Relationship set to ${relationshipLabel(next).toLowerCase()}`,
              );
            }}
            disabled={write.busy
              || !relationshipDraft
              || relationshipDraft === binding.relationshipName}
            style={button(
              !write.busy && !!relationshipDraft && relationshipDraft !== binding.relationshipName,
              'plain',
            )}
          >
            Change relationship
          </button>
        </div>
      )}

      {dialogOpen && (
        <BindOrganizationDialog
          mode="from-object"
          object={{
            id: objectId,
            name: objectName,
            objectType,
            countryIso2: countryIso2 ?? record?.alpha2 ?? null,
            parentObjectId,
            organizationId: geoLinkOrganizationId,
          }}
          countryCode={record?.numericCode ?? null}
          signer={signer}
          walletAddress={walletAddress}
          networkPassphrase={networkPassphrase}
          onClose={() => setDialogOpen(false)}
          onCompleted={refresh}
        />
      )}
    </PanelShell>
  );
};

const PanelShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{
    border: '1px solid #ddd', borderRadius: '6px', padding: '10px 12px',
    marginTop: '10px', backgroundColor: '#fbfbfb',
  }}>
    <div style={sectionTitle}>On-chain organization</div>
    <div style={{ ...hint, marginBottom: '8px' }}>
      Authoritative. The field above is GeoLink&apos;s searchable copy of it.
    </div>
    {children}
  </div>
);
